import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Button,
  useTheme
} from '@mui/material';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchPortfolio, analyzePortfolio, compareWithOptimal } from '../../slices/portfolioSlice';
import { fetchCurrentUser } from '../../slices/userSlice';

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  // Local state for initialization and tracking analysis
  const [isInitializing, setIsInitializing] = useState(true);
  const [analysisRequested, setAnalysisRequested] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get authenticated user from the auth slice (ensure the token contains the proper "sub")
  const authUser = useSelector((state) => state.auth.user);
  // Portfolio slice state
  const { data: portfolioData, analysis, comparison, status: portfolioStatus, error: portfolioError } = useSelector((state) => state.portfolio);

  // Fetch portfolio on mount
  useEffect(() => {
    if (!authUser) {
      setIsInitializing(false);
      return;
    }
    const fetchData = async () => {
      try {
        // No need to pass a userId parameter—the backend extracts it from the token.
        await dispatch(fetchPortfolio()).unwrap();
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      } finally {
        setIsInitializing(false);
      }
    };
    fetchData();
  }, [dispatch, authUser]);

  // Reset analysis trigger when portfolio data changes (using updated timestamp or portfolio content)
  useEffect(() => {
    if (portfolioData && portfolioData.updated_at) {
      setAnalysisRequested(false);
    }
  }, [portfolioData?.updated_at]);

  // Trigger analysis and comparison when portfolio is loaded
  useEffect(() => {
    const analyzeData = async () => {
      if (!analysisRequested && portfolioData?.portfolio) {
        setAnalysisRequested(true);
        try {
          await dispatch(analyzePortfolio({ portfolio: portfolioData.portfolio })).unwrap();
          await dispatch(compareWithOptimal({ portfolio: portfolioData.portfolio })).unwrap();
        } catch (error) {
          console.error('Error analyzing portfolio:', error);
        }
      }
    };
    if (!isInitializing && portfolioData?.portfolio && !analysisRequested) {
      analyzeData();
    }
  }, [dispatch, portfolioData, isInitializing, analysisRequested]);

  // Prepare data for the radar chart if comparison is available.
  const radarData = comparison
    ? [
        { metric: "Risk", score: Math.max(1, 5 * (1 - Math.abs(comparison.differences.risk.percentage))), fullMark: 5 },
        { metric: "Diversification", score: Math.max(1, 5 * (1 - Math.abs(comparison.differences.diversification.percentage))), fullMark: 5 },
        { metric: "Sector Balance", score: Math.max(1, 5 * (1 - Math.abs(comparison.differences.sector_balance.percentage))), fullMark: 5 },
        { metric: "Yield", score: Math.max(1, 5 * (1 - Math.abs(comparison.differences.yield.percentage))), fullMark: 5 },
        { metric: "Dividend Growth", score: Math.max(1, 5 * (1 - Math.abs(comparison.differences.dividend_growth.percentage))), fullMark: 5 },
        { metric: "Financial Health", score: Math.max(1, 5 * (1 - Math.abs(comparison.differences.financial_health.percentage))), fullMark: 5 }
      ]
    : [];
      
  // Custom tick renderer for RadarChart to show metric label and score.
  const renderRadarTick = (props) => {
    const { payload, x, y, textAnchor, ...rest } = props;
    const { value } = payload; // metric name (e.g., "Risk")
    const foundMetric = radarData.find((item) => item.metric === value);
    const scoreText = foundMetric ? ` (${foundMetric.score.toFixed(1)})` : '';
    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill={theme.palette.text.primary}
        {...rest}
      >
        {value}{scoreText}
      </text>
    );
  };

  const handleEditPortfolio = () => {
    navigate('/portfolio');
  };

  // Add refresh handler
  const handleRefreshAnalysis = async () => {
    if (!portfolioData?.portfolio) return;
    
    setIsRefreshing(true);
    try {
      await dispatch(analyzePortfolio({ portfolio: portfolioData.portfolio })).unwrap();
      await dispatch(compareWithOptimal({ portfolio: portfolioData.portfolio })).unwrap();
    } catch (error) {
      console.error('Error refreshing analysis:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Show a loading spinner if initialization or portfolio fetching is in progress.
  if (isInitializing || portfolioStatus === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Display error if there is a portfolio fetching error.
  if (portfolioError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{portfolioError}</Alert>
      </Container>
    );
  }

  // If no portfolio is found, prompt the user to create one.
  if (!portfolioData?.portfolio) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Welcome to Your Dashboard
        </Typography>
        <Typography color="text.secondary" paragraph>
          You haven't created a portfolio yet. Create your first portfolio to get started with analysis.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/portfolio/edit')} sx={{ mt: 2 }}>
          Create Portfolio
        </Button>
      </Container>
    );
  }

  // Main dashboard content.
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Portfolio Dashboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRefreshAnalysis}
              disabled={isRefreshing}
              startIcon={<RefreshIcon className={isRefreshing ? 'spin' : ''} />}
              sx={{
                borderRadius: 2,
                '& .spin': {
                  animation: 'spin 1s linear infinite',
                },
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
              }}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh Analysis'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditPortfolio}
              sx={{ borderRadius: 2 }}
            >
              Edit Portfolio
            </Button>
          </Box>
        </Box>
        {portfolioData?.updated_at && (
          <Typography variant="body2" color="text.secondary">
            Last updated: {new Date(portfolioData.updated_at).toLocaleDateString()}
          </Typography>
        )}
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3} alignItems="stretch">
        {/* Score Card */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              minHeight: 400,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 3
            }}
          >
            <Typography variant="h6" gutterBottom>
              Portfolio Score
            </Typography>
            <Box sx={{ position: 'relative', display: 'inline-flex', my: 3 }}>
              <CircularProgress
                variant="determinate"
                value={comparison?.score || 0}
                size={160}
                thickness={4}
                sx={{ color: theme.palette.primary.main }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h3" component="div">
                  {comparison?.score.toFixed(1) || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  out of 100
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Radar Chart */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              // height: '100%',
              minHeight: 400,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 3,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Portfolio Analysis
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, mt: 2 }}>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" tick={renderRadarTick} />
                    <PolarRadiusAxis domain={[0, 5]} tick={false} />
                    <Radar
                      name="Portfolio"
                      dataKey="score"
                      stroke={theme.palette.primary.main}
                      fill={theme.palette.primary.main}
                      fillOpacity={0.3}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Metric Cards */}
        {analysis && (
          <>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Current Yield"
                value={analysis.yield_metrics.current_yield * 100}
                unit="%"
                description="Annual dividend yield"
                icon={AccountBalanceIcon}
                trend={analysis.yield_metrics.dividend_growth_3y * 100}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Risk Level"
                value={analysis.risk * 100}
                unit="%"
                description="Portfolio volatility"
                icon={ShowChartIcon}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricCard
                title="Sharpe Ratio"
                value={analysis.sharpe_ratios.full}
                description="Risk-adjusted returns"
                icon={TrendingUpIcon}
              />
            </Grid>
          </>
        )}

        {/* Recommendations Section */}
        {comparison?.recommendations && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 3
              }}
            >
              <Typography variant="h6" gutterBottom>
                Recommendations
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* Render your recommendations here */}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}

// Helper component for displaying individual metric cards.
const MetricCard = ({ title, value, unit = '', description = '', icon: Icon, trend }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2.5
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {Icon && (
          <Box
            sx={{
              mr: 2,
              p: 1,
              borderRadius: 1,
              backgroundColor: theme.palette.primary.main + '15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ color: theme.palette.primary.main }} />
          </Box>
        )}
        <Typography variant="subtitle1" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 500 }}>
            {typeof value === 'number' ? value.toFixed(2) : value}
            {unit}
          </Typography>
          {trend !== undefined && (
            <Typography variant="body2" sx={{ ml: 1, color: trend >= 0 ? 'success.main' : 'error.main' }}>
              {trend >= 0 ? '+' : ''}
              {trend.toFixed(2)}%
            </Typography>
          )}
        </Box>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default Dashboard;
